import { useContext, useEffect } from 'react';
import { AppContext } from 'contexts/AppContext';
import { useOverviewData } from 'hooks/useOverviewData';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Wrench, AlertTriangle, ListChecks, Info, Activity } from 'lucide-react';

/**
 * Agent health — service status, tool reliability, and what is not yet measured.
 *
 * Visible to everyone with console access, which includes the client: access is
 * granted by email domain and their people are on that list. So this tab is
 * written for a mixed audience.
 *
 * The split is cause versus consequence, not good news versus bad. Every gap is
 * listed for everyone, with what is needed to close it -- that is the useful
 * part, and the part they may need to action. What internal viewers see on top
 * (`isInternal`) is the diagnosis: which column stopped being written, which
 * UPDATE targets columns that do not exist, which service went quiet. Without
 * context that reads as a defect list; with it, it is a work queue.
 */

/** What the console cannot measure, why, and what would unblock it. */
const GAPS = [
  {
    metric: 'The Usage Funnel (Opened \u2192 Asked \u2192 Saw a Product \u2192 Added to Cart)',
    why: 'Opens and messages are both recorded. They cannot be joined: the two were written with different session-id formats, so a rate across the two has no reliable denominator.',
    unblock: 'Aligning the session id sent on open with the one sent on a message. Written for web; mobile needs the same change. Rates apply to traffic after it ships -- history cannot be stitched retroactively.',
    cause: 'Only 35 of 6,053 open sessions appear in the message table at all (0.58%) -- a namespace mismatch, not a drop-off. The earlier reading of this gap ("five of 1,523 sessions ever sent a message") was measuring that same broken join.',
  },
  {
    metric: 'Abandon Rate',
    why: 'The denominator exists -- opens are recorded -- but an open cannot yet be tied to whether a message followed it.',
    unblock: 'The same session-id alignment as the funnel above.',
  },
  {
    metric: 'Suggested Question vs Typed',
    why: 'Nothing distinguishes a tapped suggestion from a typed message.',
    unblock: 'A source field on the message event.',
  },
  {
    metric: 'Session Length, Time to First Message',
    why: 'There are no session start or end events -- only the messages themselves.',
    unblock: 'Session lifecycle events.',
  },
  {
    metric: 'Follow-up Rate',
    why: 'Nothing marks which messages are replies, so a follow-up looks identical to a new question.',
    unblock: 'A reply marker on the message event.',
  },
  {
    metric: 'Product Clicks',
    why: 'Product clicks are not currently being recorded.',
    unblock: 'A fix in the logging service, once the column mapping is confirmed.',
    cause: 'cart_click has 0 rows ever -- /cart-click UPDATEs cart_item and cart_qty, which do not exist on that table, so every call raises into a fire-and-forget task.',
  },
  {
    metric: 'Conversion Rate, Views to Cart',
    why: 'Products shown and products added use different product code formats, so they cannot be matched to each other reliably.',
    unblock: 'A shared product identifier, or a mapping between the two.',
    cause: 'Catalog stores brand-prefixed SKUs (HPPSP2670007X10), cart stores display names (... | TVP700C). 0 of 117 match outright, 57 of 100 after splitting on the pipe -- and the misses are indistinguishable from genuine non-conversions.',
  },
  {
    metric: 'Email Funnel',
    why: 'Email actions are not recorded anywhere.',
    unblock: 'Tracking on the email modal.',
  },
  {
    metric: 'Interruptions During a Voice Answer',
    why: 'The voice bridge writes the field, but it came back empty on every turn measured so far, so this reads as unknown rather than as zero.',
    unblock: 'Confirming when the model reports an interruption, then recording it on the turn.',
    cause: 'Sessions, turns, length and connect time ARE now on the Voice tab. This is the one voice figure still missing, and it is reported as unknown rather than 0 -- 0 of 0 recorded and 0 of 40 recorded are different facts.',
  },
  {
    metric: 'Cost per Conversation',
    why: 'Output tokens are recorded as zero on voice turns, so a cost figure would be confidently wrong rather than merely absent.',
    unblock: 'Reading the usage field the Live API actually reports output tokens in.',
    cause: 'Prompt and total populate normally; measured 2026-08-24, total 9689 against prompt 9318, so the missing 371 sits in a field the recorder does not read. Text turns report output correctly, so this is voice-only.',
  },
];

const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);

export default function AgentHealthTab() {
  const { selectedClient, dateRange, platformFilter, isInternal } = useContext(AppContext);
  const { tools, toolsLoading, fetchTools, health, healthLoading, fetchHealth } = useOverviewData();

  useEffect(() => {
    fetchTools();
  }, [selectedClient, dateRange, platformFilter, fetchTools]);

  // Health has no date or platform dimension -- "is it up" is the same question
  // whatever window you are looking at, so this fires once rather than on every
  // filter change.
  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const failing = (tools || []).filter((t) => t.success_pct !== null && t.success_pct < 100);

  return (
    <div className="space-y-5">

      {/* ── Endpoint liveness ────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Services
            {health && (
              <Badge
                variant="outline"
                className={`font-normal text-[10px] px-1.5 py-0 ${
                  health.reachable === health.total
                    ? 'border-emerald-400 text-emerald-700 dark:text-emerald-300'
                    : 'border-red-400 text-red-700 dark:text-red-300'}`}
              >
                {health.reachable} of {health.total} responding
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {healthLoading && <div className="text-sm text-gray-500 py-6">Checking…</div>}
          {!healthLoading && !health && (
            <div className="text-sm text-gray-500 py-6">Could not run the check.</div>
          )}
          {!healthLoading && health && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-zinc-200 dark:border-zinc-800">
                  <th className="text-left font-normal py-1.5">Service</th>
                  <th className="text-left font-normal">Group</th>
                  <th className="text-right font-normal">Response</th>
                  <th className="text-right font-normal">Time</th>
                </tr>
              </thead>
              <tbody>
                {health.endpoints.map((e) => (
                  <tr key={e.url} className="border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                    <td className="py-1.5 pr-2 truncate max-w-[240px]" title={isInternal ? e.url : e.name}>
                      {e.name}
                      {e.cold_start && (
                        <Badge variant="outline" className="ml-2 font-normal text-[10px] px-1.5 py-0">
                          Cold Start
                        </Badge>
                      )}
                    </td>
                    <td className="text-xs text-gray-500">{e.group}</td>
                    <td className={`text-right tabular-nums ${e.reachable ? '' : 'text-red-600 font-medium'}`}>
                      {e.reachable ? e.status : (e.state === 'timeout' ? 'no response' : 'unreachable')}
                    </td>
                    <td className="text-right tabular-nums text-gray-500">{e.response_ms}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="pt-3 text-xs text-gray-500 flex gap-2">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              Checked just now, when this page loaded — this is not uptime monitoring, and
              nothing is watched while the page is closed. <strong>A 404 means healthy</strong>:
              these are API services with no page at their root. Services scale down when idle,
              so a “cold start” badge means the first request was slow to wake it, not that it
              was broken.
            </span>
          </p>
        </CardContent>
      </Card>

      {/* ── Tool reliability ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Tool Reliability
          </CardTitle>
        </CardHeader>
        <CardContent>
          {toolsLoading && <div className="text-sm text-gray-500 py-6">Loading…</div>}
          {!toolsLoading && (tools || []).length === 0 && (
            <div className="text-sm text-gray-500 py-6">No tool calls recorded in this range.</div>
          )}
          {!toolsLoading && (tools || []).length > 0 && (
            <>
              {failing.length > 0 && (
                <div className="flex gap-2 mb-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2.5 text-xs text-amber-900 dark:text-amber-200">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    {failing.map((t) => `${t.tool} (${t.success_pct}%)`).join(', ')} —
                    below 100% success.
                    {isInternal && ' Historically the error log was dominated by Klevu search timeouts, and the same subsystem is behind most "couldn\'t find any products" answers on Quality.'}
                  </span>
                </div>
              )}
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-zinc-200 dark:border-zinc-800">
                    <th className="text-left font-normal py-1.5">Tool</th>
                    <th className="text-right font-normal">Calls</th>
                    <th className="text-right font-normal">Success</th>
                    <th className="text-right font-normal">p95</th>
                    <th className="text-right font-normal">Worst</th>
                  </tr>
                </thead>
                <tbody>
                  {tools.map((t) => (
                    <tr key={t.tool} className="border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                      <td className="py-1.5 pr-2 truncate max-w-[220px]" title={t.tool}>{t.tool}</td>
                      <td className="text-right tabular-nums">{t.calls}</td>
                      <td className={`text-right tabular-nums ${t.success_pct !== null && t.success_pct < 100 ? 'text-red-600 font-medium' : ''}`}>
                        {pct(t.success_pct)}
                      </td>
                      <td className={`text-right tabular-nums ${t.p95_seconds !== null && t.p95_seconds >= 10 ? 'text-red-600 font-medium' : ''}`}>
                        {t.p95_seconds !== null ? `${t.p95_seconds}s` : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="text-right tabular-nums text-gray-500">
                        {t.max_seconds !== null ? `${t.max_seconds}s` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Errors ─── internal only ─────────────────────────────────────
        * This card is a diagnosis of our own logging, not of the assistant. To
        * a client it reads as "something is broken" with no way to tell that it
        * predates the current work and does not affect what they see. */}
      {isInternal && (
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-gray-500">
            <AlertTriangle className="h-4 w-4" />
            Errors
            <Badge variant="outline" className="font-normal text-[10px] px-1.5 py-0 whitespace-nowrap">
              Not Being Recorded
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-500 space-y-2">
          <p>
            The <code>errors</code> column carried 8–23% of turns through late 2025 and has
            been <strong>exactly zero since November</strong> — while tool success sits below
            100% and roughly 1,700 Klevu timeouts are recorded in the older rows.
          </p>
          <p className="text-xs">
            Ten silent months alongside tools that are still failing means something stopped
            writing the column, not that the errors stopped. Same release window as two other
            things that went quiet — worth one investigation, not three.
          </p>
        </CardContent>
      </Card>
      )}

      {/* ── Instrumentation backlog — INTERNAL ONLY ───────────────────────
        *
        * The WHOLE card is gated now, not just the `cause` lines inside it.
        *
        * It previously rendered for everyone with client-safe wording and the
        * diagnosis hidden. That was still the wrong thing to put in front of a
        * client: read together, the list is an inventory of what the product
        * cannot do yet. That is a roadmap conversation to have deliberately,
        * not something to come across on a dashboard tab.
        *
        * The TAB stays visible to everyone. Service status and tool reliability
        * are useful to the client and are genuinely reassuring; it is only the
        * backlog that is ours.
        */}
      {isInternal && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ListChecks className="h-4 w-4" />
              What we still cannot measure
              <span className="text-[10px] font-normal uppercase tracking-wide px-1.5 py-0.5 rounded bg-zinc-200 dark:bg-zinc-700 text-gray-600 dark:text-gray-300">
                Internal
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500 mb-3 flex gap-2">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                Metrics the design asks for that cannot be reported yet, each with what
                would be needed to enable it. This is a list of tracking still to be
                added, not of things that have gone wrong. Not shown to client accounts.
              </span>
            </p>
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {GAPS.map((g) => (
                <li key={g.metric} className="py-3">
                  <div className="text-sm font-medium mb-1">{g.metric}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-300 mb-1">{g.why}</div>
                  <div className="text-xs text-emerald-700 dark:text-emerald-400">
                    Needs: {g.unblock}
                  </div>
                  {g.cause && (
                    <div className="text-xs text-gray-500 mt-1 pl-3 border-l-2 border-zinc-300 dark:border-zinc-700">
                      {g.cause}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
